// Shared full-viewport modal backdrop used by every popup on the
// dashboard + deal tracker. Exists to collapse ~8 copies of the same
// "position:fixed inset:0 + onClick close + blur" block into one
// component so z-index / backdrop tint / padding stay consistent
// (and so adding things like escape-to-close happens in one place
// later on). Child card is caller-owned — only the outer backdrop
// is standardized.
import { useEffect, useId } from 'react';
import { registerOverlay, unregisterOverlay } from '../utils/modalLayer';

export default function ModalOverlay({
  onClose,
  zIndex = 1400,
  // "Improve this" sits one rung above whatever is open, so it needs to
  // know what is open. The report modal itself opts out: counting it
  // would make it chase its own z-index upward every time it rendered.
  register = true,
  padding = '1rem',
  backdrop = 'rgba(0,10,40,0.6)',
  blur = 3,
  alignItems = 'center',
  justifyContent = 'center',
  children,
}) {
  const id = useId();
  useEffect(() => {
    if (!register) return undefined;
    registerOverlay(id, zIndex);
    return () => unregisterOverlay(id);
  }, [id, zIndex, register]);

  return (
    <div
      // Only a click that lands on the backdrop itself closes. This was
      // a bare onClick, so every click inside the dialog bubbled up and
      // dismissed it — clicking into a textarea to type closed the
      // window. Consumers that already stopPropagation are unaffected.
      onClick={(event) => { if (event.target === event.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex,
        background: backdrop,
        backdropFilter: `blur(${blur}px)`,
        display: 'flex', alignItems, justifyContent,
        padding,
      }}
    >
      {children}
    </div>
  );
}
